import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import { Checkbox } from "ui/checkbox";
import ProgramTemplatesActionsMenu from "containers/admin/clients/client/program-templates-page/components/program-templates-actions-menu/program-templates-actions-menu";

import type { CampaignTouchpointSummaryDTO } from "generated/schemas";
import { getStatuses } from "components/partials/selected-items/selected-items.helper";
import { TouchpointStatusDictionaryItem } from "hooks/api/dictionary";
import { getTouchpointPaths } from "utilities/app-routes";
import { getDisplayTouchpointType, getDisplayTouchpointIcon } from "utilities";
import {
  handleIsAllSelected,
  handleIsSelected,
} from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import { useProgramTemplatesSelection } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-selection-context";
import { formatDateTime } from "utilities/date";

import { TouchpointType } from "types/touchpoint";

const headerStyles = "text-gray-600 font-bold leading-5 pl-4";

// The table is unpaged, so every selection lives in the first page entry.
const PAGE_INDEX = 0;

const SelectAllTemplatesCheckbox = () => {
  const { allSelectedData, setAllSelectedData, displayedTemplates } = useProgramTemplatesSelection();
  const currentAllSelectedData = allSelectedData[PAGE_INDEX];
  const isAllSelected =
    currentAllSelectedData?.isAllSelected ||
    (displayedTemplates.length > 0 &&
      currentAllSelectedData?.selectedTouchpoints?.length === displayedTemplates.length &&
      displayedTemplates.every((tp) =>
        currentAllSelectedData.selectedTouchpoints.some((selectedTp) => tp.id === selectedTp.id),
      ));

  const handleChange = () => {
    setAllSelectedData(handleIsAllSelected(allSelectedData, PAGE_INDEX, displayedTemplates));
  };

  return (
    <Checkbox
      size="sm"
      aria-label="Select all templates"
      checked={isAllSelected}
      indeterminate={currentAllSelectedData?.selectedTouchpoints?.length > 0}
      onChange={handleChange}
    />
  );
};

const SelectTemplateCheckbox = ({ template }: { template: CampaignTouchpointSummaryDTO }) => {
  const { allSelectedData, setAllSelectedData, displayedTemplates } = useProgramTemplatesSelection();
  const currentPageData = allSelectedData[PAGE_INDEX] ?? {
    isAllSelected: false,
    page: PAGE_INDEX,
    selectedTouchpoints: [],
  };

  const isSelected =
    (currentPageData.isAllSelected && !currentPageData.selectedTouchpoints.length) ||
    currentPageData.selectedTouchpoints.some((tp) => tp.id === template.id);

  const handleChange = () => {
    const { id, name, type, latestVersion } = template;

    if (!id || !name) return;

    const newAllSelectedData = handleIsSelected({
      allSelectedData,
      pageIndex: PAGE_INDEX,
      displayedTemplates,
      selectedTemplate: {
        id,
        name,
        type: type || "",
        latestVersion: latestVersion
          ? {
              version: latestVersion.version ?? 0,
              status: latestVersion.status || "",
              id: latestVersion.id || "",
              applyClientTheme: latestVersion.applyClientTheme,
            }
          : null,
      },
    });

    setAllSelectedData(newAllSelectedData);
  };

  return (
    <Checkbox size="sm" aria-label="Select template" checked={isSelected} onChange={handleChange} />
  );
};

interface CreateProgramTemplatesTableColumnsProps {
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
  clientId: string;
  programTemplatesCampaignId: string;
  onRefetch?: () => void;
}

export const createProgramTemplatesTableColumns = ({
  touchpointVersionStatuses,
  clientId,
  programTemplatesCampaignId,
  onRefetch,
}: CreateProgramTemplatesTableColumnsProps): ColumnDef<CampaignTouchpointSummaryDTO>[] => [
  {
    id: "checkbox",
    enableSorting: false,
    header: () => <SelectAllTemplatesCheckbox />,
    cell: ({ row }) => <SelectTemplateCheckbox template={row.original} />,
    size: 50,
    meta: {
      className: "px-0 pl-4",
      headerClassName: "px-0 pl-4",
      isFixedWidth: true,
    },
  },
  {
    id: "type",
    header: "Type",
    accessorKey: "type",
    enableSorting: true,
    cell: ({ row }) => {
      const type = row.original.type as unknown as TouchpointType;
      const label = getDisplayTouchpointType(type);
      const icon = getDisplayTouchpointIcon(type);

      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          {icon}
          {label}
        </div>
      );
    },
    size: 130,
    meta: {
      headerClassName: headerStyles,
      isFixedWidth: true,
    },
  },
  {
    id: "name",
    header: "Template name",
    accessorKey: "name",
    enableSorting: true,
    cell: ({ row }) => {
      const touchpointId = row.original.id;

      const { touchpointPreviewPath } = getTouchpointPaths({
        clientId,
        campaignId: programTemplatesCampaignId,
        touchpointId,
      });
      return (
        <Link
          to={touchpointPreviewPath}
          state={{ isProgramTemplate: true }}
          className="text-aqua-600 hover:text-aqua-700 font-semibold text-base">
          {row.original.name}
        </Link>
      );
    },
    size: 400,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "latestVersion",
    header: "Newest version status",
    enableSorting: false,
    cell: ({ row }) => {
      const latestVersionStatus = row.original.latestVersion?.status || "";
      const status = getStatuses(latestVersionStatus, touchpointVersionStatuses);

      return status ? <StatusIndicator versionStatuses={status} /> : null;
    },
    size: 200,
    meta: {
      headerClassName: headerStyles,
      isFixedWidth: true,
    },
  },
  {
    id: "lastModifiedDate",
    header: "Last updated",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
    ),
    size: 180,
    meta: {
      headerClassName: headerStyles,
      isFixedWidth: true,
    },
  },
  {
    id: "actions",
    header: "Action",
    enableSorting: false,
    cell: ({ row }) => (
      <ProgramTemplatesActionsMenu
        template={row.original}
        programTemplatesCampaignId={programTemplatesCampaignId}
        onCloneSuccess={onRefetch}
      />
    ),
    size: 96,
    meta: {
      className: "pr-8",
      headerClassName: headerStyles,
      isHeaderCentered: true,
      isPinnedRight: true,
    },
  },
];
